// Page registry — defines all 26 unique topics with EN + ZH slugs
// Each page lives at /<slug> for English and /zn/<slug> for Chinese.
// Page id is used as key for content lookup.

export const PAGES = [
  { id: "home", slug: "", type: "home", icon: "Home" },
  { id: "about", slug: "about-us", type: "about", icon: "Users" },
  { id: "services-pricing", slug: "services-and-price", type: "pricing", icon: "Tag" },
  { id: "contact", slug: "contact-us", type: "contact", icon: "Mail" },

  // Services (detail pages)
  { id: "melbourne-accounting-firm", slug: "melbourne-accounting-firm", type: "service", group: "services" },
  { id: "brisbane-accounting-firm", slug: "brisbane-accounting-firm", type: "service", group: "services" },
  { id: "canterbury-accounting", slug: "canterbury-accounting", type: "service", group: "services" },
  { id: "chinese-accountants-aus", slug: "chinese-accountants-australia", type: "service", group: "services" },
  { id: "melbourne-accounting-tax", slug: "melbourne-accounting-tax", type: "service", group: "services" },
  { id: "box-hill-accountant", slug: "box-hill-accountant", type: "service", group: "services" },

  // Tax services
  { id: "individual-tax", slug: "individual-tax-services", type: "service", group: "tax" },
  { id: "melbourne-company-personal-tax", slug: "melbourne-company-personal-tax", type: "service", group: "tax" },
  { id: "company-tax", slug: "company-tax-services", type: "service", group: "tax" },
  { id: "small-business-tax", slug: "small-business-tax", type: "service", group: "tax" },
  { id: "business-tax", slug: "business-tax", type: "service", group: "tax" },
  { id: "tax-deadlines", slug: "australian-tax-deadlines", type: "service", group: "tax" },

  // Compliance
  { id: "bas-lodgement", slug: "bas-lodgement", type: "service", group: "compliance" },
  { id: "gst-registration", slug: "gst-registration", type: "service", group: "compliance" },
  { id: "tfn", slug: "tfn", type: "service", group: "compliance" },
  { id: "abn", slug: "how-to-apply-abn", type: "service", group: "compliance" },

  // Setup & advisory
  { id: "company-setup", slug: "company-setup", type: "service", group: "advisory" },
  { id: "smsf", slug: "smsf", type: "service", group: "advisory" },
  { id: "family-trust", slug: "family-trust-guide", type: "service", group: "advisory" },

  // Insights
  { id: "negative-gearing", slug: "negative-gearing", type: "service", group: "insights" },
  { id: "home-office-tax", slug: "home-office-tax", type: "service", group: "insights" },
  { id: "business-travel-tax", slug: "business-travel-tax", type: "service", group: "insights" },

  // Insights landing page (blog index)
  { id: "insights", slug: "insights", type: "insights-list" },

  // Blog posts (insights long-form) — surfaced only via the insights landing page
  { id: "sole-trader-vs-company", slug: "sole-trader-vs-company", type: "service", group: "blog" },
  { id: "division-7a-director-loans", slug: "division-7a-director-loans", type: "service", group: "blog" },
  { id: "cash-flow-management", slug: "cash-flow-management-small-business", type: "service", group: "blog" },
  { id: "single-touch-payroll-stp", slug: "single-touch-payroll-stp", type: "service", group: "blog" },
  { id: "hospitality-tax-deductions", slug: "hospitality-tax-deductions", type: "service", group: "blog" },
  { id: "tpar-taxable-payments", slug: "taxable-payments-annual-report-tpar", type: "service", group: "blog" },
  { id: "car-personal-or-company", slug: "car-personal-or-company-name", type: "service", group: "blog" },
  { id: "payday-super", slug: "payday-super-australia-2026", type: "service", group: "blog" },
  { id: "business-structure-guide", slug: "business-structure-sole-trader-partnership-company-trust-australia", type: "service", group: "blog" },
  { id: "in-house-vs-outsourced-accounting", slug: "in-house-vs-outsourced-accounting-australia-cost-comparison", type: "service", group: "blog" },
  { id: "aml-ctf-tranche-2", slug: "aml-ctf-tranche-2-reforms-australia-2026-guide", type: "service", group: "blog" },
];

export const getPageById = (id) => PAGES.find((p) => p.id === id);

export const pathFor = (id, lang) => {
  const page = getPageById(id);
  if (!page) return "/";
  const base = page.slug ? `/${page.slug}` : "/";
  return lang === "zh" ? (page.slug ? `/zn/${page.slug}` : "/zn") : base;
};

// Group structure for mega-menu
export const NAV_GROUPS = {
  services: ["melbourne-accounting-firm", "brisbane-accounting-firm", "canterbury-accounting", "box-hill-accountant", "chinese-accountants-aus", "melbourne-accounting-tax"],
  tax: ["individual-tax", "melbourne-company-personal-tax", "company-tax", "small-business-tax", "business-tax", "tax-deadlines"],
  compliance: ["bas-lodgement", "gst-registration", "tfn", "abn"],
  advisory: ["company-setup", "smsf", "family-trust"],
  insights: ["negative-gearing", "home-office-tax", "business-travel-tax"],
  // Long-form blog posts (not surfaced in the mega-menu)
  blog: ["payday-super", "sole-trader-vs-company", "division-7a-director-loans", "cash-flow-management", "single-touch-payroll-stp", "hospitality-tax-deductions", "tpar-taxable-payments", "car-personal-or-company", "business-structure-guide", "in-house-vs-outsourced-accounting", "aml-ctf-tranche-2"],
};

// Page IDs that should NOT appear in the mega-menu's main list,
// but may still be surfaced in the Featured column or via related services.
export const NAV_HIDDEN_FROM_LIST = new Set([
  "melbourne-company-personal-tax",
]);

// Stock imagery (Unsplash) keyed by page id — used as hero banners/cards
export const PAGE_IMAGES = {
  home: "/images/stock/1554224155-6726b3ff858f.jpg",
  about: "/images/stock/1521737711867.jpg",
  "services-pricing": "/images/stock/1554224154-26032ffc0d07.jpg",
  contact: "/images/stock/1486406146926.jpg",
  "melbourne-accounting-firm": "/images/stock/1486406146926.jpg",
  "brisbane-accounting-firm": "/images/stock/1572025442646.jpg",
  "canterbury-accounting": "/images/stock/1497366216548.jpg",
  "chinese-accountants-aus": "/images/stock/1556761175-5973dc0f32e7.jpg",
  "melbourne-accounting-tax": "/images/stock/1450101499163.jpg",
  "individual-tax": "/images/stock/1554224155-1696413565d3.jpg",
  "melbourne-company-personal-tax": "/images/stock/1573164713988.jpg",
  "company-tax": "/images/stock/1556761175-b413da4baf72.jpg",
  "small-business-tax": "/images/stock/1521791136064.jpg",
  "business-tax": "/images/stock/1542744173.jpg",
  "tax-deadlines": "/images/stock/1506784983877.jpg",
  "bas-lodgement": "/images/stock/1554224154-22dec7ec8818.jpg",
  "gst-registration": "/images/stock/1551836022.jpg",
  "tfn": "/images/stock/1450101499163.jpg",
  "abn": "/images/stock/1454165804606.jpg",
  "company-setup": "/images/stock/1552664730.jpg",
  "smsf": "/images/stock/1579621970795.jpg",
  "family-trust": "/images/stock/1511895426328.jpg",
  "negative-gearing": "/images/stock/1560518883.jpg",
  "home-office-tax": "/images/stock/1593642632559.jpg",
  "business-travel-tax": "/images/stock/1436491865332.jpg",
  insights: "/images/blog/cash-flow-management.jpg",
  "sole-trader-vs-company": "/images/blog/sole-trader-vs-company.jpg",
  "division-7a-director-loans": "/images/blog/division-7a-director-loans.jpg",
  "cash-flow-management": "/images/blog/cash-flow-management.jpg",
  "single-touch-payroll-stp": "/images/blog/single-touch-payroll-stp.jpg",
  "hospitality-tax-deductions": "/images/blog/hospitality-tax-deductions.jpg",
  "tpar-taxable-payments": "/images/blog/tpar-taxable-payments.jpg",
  "car-personal-or-company": "/images/blog/car-personal-or-company.jpg",
  "payday-super": "/images/blog/payday-super-hero.jpg",
  "business-structure-guide": "/images/blog/business-structure-guide.jpg",
  "in-house-vs-outsourced-accounting": "/images/blog/in-house-vs-outsourced-accounting.jpg",
  "aml-ctf-tranche-2": "/images/blog/aml-ctf-tranche-2.jpg",
  "box-hill-accountant": "/images/stock/1497366216548.jpg",
};

export const HOME_VIDEO = "/videos/hero-home.mp4";
